import * as React from "react";
import { StatusAdornment } from "../display/StatusAdornment";

import { useFormFieldProps } from "../salt/form-field-context";
import { useControlled } from "./useControlled";
import "./input.css";

/**
 * Input, replacing `@salt-ds/core`'s for the 439 elements this app renders.
 *
 * ── IT IS A DIV THAT CONTAINS AN INPUT, NOT AN INPUT ──────────────────────────────────
 *
 * The outer div carries the border, the focus ring and the state classes; the inner `<input>`
 * is transparent and borderless. That is what lets adornments, the validation icon and the
 * activation indicator sit INSIDE the field's frame. It also means `className` and `style` go
 * to the wrapper while `inputProps` reaches the control — a split every call site relies on.
 *
 * ── WHAT IT MERGES FROM THE SURROUNDING FORMFIELD ─────────────────────────────────────
 *
 * `disabled`, `readOnly` and `validationStatus` may come from either the prop or the field,
 * and the field WINS for validation status: a field marked invalid must not be contradicted by
 * a child that thinks it is fine.
 *
 * The aria ids are CONCATENATED rather than overridden. The field supplies the label and
 * helper-text ids; a caller may add more via `inputProps`. Taking one and dropping the other
 * is how a control ends up describing itself while ignoring the error message beneath it.
 *
 * ── THE EM DASH IS A FEATURE ──────────────────────────────────────────────────────────
 *
 * An empty read-only input renders `emptyReadOnlyMarker` ("—"). A blank read-only box is
 * indistinguishable from one that failed to load; the dash says "this is empty on purpose".
 */
export type InputVariant = "primary" | "secondary" | "tertiary";
export type InputValidationStatus = "error" | "warning" | "success";

/*
 * Mirrors the vendor's declaration rather than a hand-written approximation. Three things
 * were narrower on the first attempt and each was a real call site, not a hypothetical:
 *
 *   value / defaultValue   React's input value is `string | number | readonly string[]`,
 *                          and call sites pass numbers.
 *   variant                "tertiary" exists and `src/components/ui/input.tsx` uses it.
 *   inputProps             must admit `data-*`; the design showroom sets `data-testid` there.
 *
 * This is the second time in this migration that inferring a prop type from the IMPLEMENTATION
 * instead of reading the declaration produced a silently narrower API.
 */
type DataAttributes = Record<`data-${string}`, unknown>;

export interface InputProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "defaultValue">,
    Pick<
      React.ComponentPropsWithoutRef<"input">,
      "disabled" | "value" | "defaultValue" | "placeholder"
    > {
  bordered?: boolean;
  emptyReadOnlyMarker?: string;
  endAdornment?: React.ReactNode;
  inputProps?: Partial<React.InputHTMLAttributes<HTMLInputElement>> & DataAttributes;
  inputRef?: React.Ref<HTMLInputElement>;
  readOnly?: boolean;
  startAdornment?: React.ReactNode;
  textAlign?: "left" | "center" | "right";
  validationStatus?: InputValidationStatus;
  variant?: InputVariant;
}

function join(...values: (string | undefined)[]): string | undefined {
  const out = values.filter(Boolean).join(" ");
  return out || undefined;
}

export const Input = React.forwardRef<HTMLDivElement, InputProps>(function Input(
  {
    "aria-activedescendant": ariaActiveDescendant,
    "aria-expanded": ariaExpanded,
    "aria-owns": ariaOwns,
    bordered = false,
    className,
    disabled,
    emptyReadOnlyMarker = "—",
    endAdornment,
    id,
    inputProps = {},
    inputRef,
    placeholder,
    readOnly: readOnlyProp,
    role,
    startAdornment,
    style,
    textAlign = "left",
    validationStatus: validationStatusProp,
    value: valueProp,
    // Mirrors the vendor exactly: an uncontrolled input starts as "" so it is never a
    // controlled-to-uncontrolled switch, while a controlled one must NOT also carry a default.
    defaultValue: defaultValueProp = valueProp === undefined ? "" : undefined,
    variant = "primary",
    ...rest
  },
  ref,
) {
  const {
    a11yProps: {
      "aria-describedby": formFieldDescribedBy,
      "aria-labelledby": formFieldLabelledBy,
    } = {},
    disabled: formFieldDisabled,
    readOnly: formFieldReadOnly,
    necessity: formFieldRequired,
    validationStatus: formFieldValidationStatus,
  } = useFormFieldProps();

  const isDisabled = disabled || formFieldDisabled;
  const isReadOnly = readOnlyProp || formFieldReadOnly;
  const validationStatus = formFieldValidationStatus ?? validationStatusProp;

  const [focused, setFocused] = React.useState(false);

  const isEmptyReadOnly = isReadOnly && !defaultValueProp && !valueProp;
  const defaultValue = isEmptyReadOnly ? emptyReadOnlyMarker : defaultValueProp;

  const {
    "aria-describedby": inputDescribedBy,
    "aria-labelledby": inputLabelledBy,
    onBlur,
    onChange,
    onFocus,
    required: inputPropsRequired,
    className: inputClassName,
    ...restInputProps
  } = inputProps;

  const isRequired = formFieldRequired
    ? ["required", "asterisk"].includes(formFieldRequired)
    : inputPropsRequired;

  const [value, setValue] = useControlled<React.InputHTMLAttributes<HTMLInputElement>["value"]>({
    controlled: valueProp,
    default: defaultValue ?? "",
  });

  const classes = ["saltInput", `saltInput-${variant}`];
  if (!isDisabled && focused) classes.push("saltInput-focused");
  if (isDisabled) classes.push("saltInput-disabled");
  if (isReadOnly) classes.push("saltInput-readOnly");
  if (validationStatus) classes.push(`saltInput-${validationStatus}`);
  if (bordered) classes.push("saltInput-bordered");
  if (className) classes.push(className);

  return (
    <div
      className={classes.join(" ")}
      ref={ref}
      style={{ "--input-textAlign": textAlign, ...style } as React.CSSProperties}
      {...rest}
    >
      {startAdornment ? (
        <div className="saltInput-startAdornmentContainer">{startAdornment}</div>
      ) : null}
      <input
        aria-activedescendant={ariaActiveDescendant}
        aria-describedby={join(formFieldDescribedBy, inputDescribedBy)}
        aria-expanded={ariaExpanded}
        aria-labelledby={join(formFieldLabelledBy, inputLabelledBy)}
        aria-owns={ariaOwns}
        className={inputClassName ? `saltInput-input ${inputClassName}` : "saltInput-input"}
        disabled={isDisabled}
        id={id}
        onBlur={(event) => {
          onBlur?.(event);
          setFocused(false);
        }}
        onChange={(event) => {
          setValue(event.target.value);
          onChange?.(event);
        }}
        // Not attached when disabled: a `focusableWhenDisabled`-style control can still receive
        // focus, and painting the focus ring on a field nobody can type in is a lie.
        onFocus={
          !isDisabled
            ? (event) => {
                onFocus?.(event);
                setFocused(true);
              }
            : undefined
        }
        placeholder={placeholder}
        readOnly={isReadOnly}
        ref={inputRef}
        role={role}
        tabIndex={isDisabled ? -1 : 0}
        value={value}
        {...restInputProps}
        required={isRequired}
      />
      {/* Suppressed when disabled, for the same reason the helper text's icon is. */}
      {!isDisabled && validationStatus ? <StatusAdornment status={validationStatus} /> : null}
      {endAdornment ? <div className="saltInput-endAdornmentContainer">{endAdornment}</div> : null}
      {/* The animated underline that marks focus. Empty by design; the CSS draws it. */}
      <div className="saltInput-activationIndicator" />
    </div>
  );
});
